import { Injectable } from '@angular/core';

/**
 * Service responsável por recuperar animações/áudios travados
 * 
 * Responsabilidades:
 * - Parar todos os áudios em reprodução
 * - Limpar animações congeladas
 * - Resetar estado visual para idle
 * - Limpar timers e observables ativos
 * 
 * Princípios SOLID:
 * - SRP: Responsabilidade única de recovery de animações
 * - OCP: Extensível para novos tipos de mídia
 * - ISP: Interface segregada (métodos específicos)
 */
@Injectable({
  providedIn: 'root',
})
export class AnimationRecoveryService {
  constructor() {}

  /**
   * Para todos os áudios HTMLAudioElement em reprodução
   * 
   * MELHORIA: Scope específico para elementos de alertas
   */
  stopAllHTMLAudio(): void {
    try {
      // Buscar apenas áudios dentro de componentes de alerta
      const alertContainers = document.querySelectorAll(
        'app-donation, app-sale, app-alert-display, app-goal'
      );
      
      let stoppedCount = 0;
      
      alertContainers.forEach((container) => {
        const audioElements = container.querySelectorAll('audio');
        
        audioElements.forEach((audio) => {
          if (!audio.paused) {
            console.log('⏸️ Parando áudio HTML:', audio.src);
            audio.pause();
            audio.currentTime = 0;
            audio.src = ''; // Limpar source
            stoppedCount++;
          }
        });
      });

      console.log(`✅ ${stoppedCount} elementos de áudio HTML parados`);
    } catch (error) {
      console.error('❌ Erro ao parar áudios HTML:', error);
    }
  }

  /**
   * Para o Speech Synthesis (TTS nativo do browser)
   */
  stopSpeechSynthesis(): void {
    try {
      if ('speechSynthesis' in window) {
        if (window.speechSynthesis.speaking) {
          console.log('⏸️ Parando Speech Synthesis');
          window.speechSynthesis.cancel();
        }
      }
    } catch (error) {
      console.error('❌ Erro ao parar Speech Synthesis:', error);
    }
  }

  /**
   * Para todos os elementos <video> em reprodução
   */
  stopAllVideos(): void {
    try {
      const videoElements = document.querySelectorAll('video');
      
      videoElements.forEach((video) => {
        if (!video.paused) {
          console.log('⏸️ Parando vídeo:', video.src);
          video.pause();
          video.currentTime = 0;
        }
      });

      console.log(`✅ ${videoElements.length} vídeos parados`);
    } catch (error) {
      console.error('❌ Erro ao parar vídeos:', error);
    }
  }

  /**
   * Limpa animações CSS congeladas
   * 
   * MELHORIA: Scope específico para elementos de alertas
   */
  resetFrozenAnimations(): void {
    try {
      // Buscar apenas elementos de alerta com animações
      const alertContainers = document.querySelectorAll(
        'app-donation, app-sale, app-alert-display, app-goal'
      );
      
      let resetCount = 0;
      
      alertContainers.forEach((container) => {
        // Buscar elementos animados dentro do container
        const animatedElements = container.querySelectorAll(
          '[class*="animate"], [class*="fade"], [class*="slide"], [class*="bounce"]'
        );
        
        animatedElements.forEach((element) => {
          // Forçar reflow para resetar animação
          const htmlElement = element as HTMLElement;
          htmlElement.style.animation = 'none';
          
          // Trigger reflow
          void htmlElement.offsetHeight;
          
          // Restaurar animação (se necessário)
          htmlElement.style.animation = '';
          resetCount++;
        });
      });

      console.log(`✅ ${resetCount} animações resetadas`);
    } catch (error) {
      console.error('❌ Erro ao resetar animações:', error);
    }
  }

  /**
   * Remove elementos DOM temporários (alertas travados)
   */
  clearTemporaryAlertElements(): void {
    try {
      // Buscar componentes de alerta específicos do sistema
      const alertContainers = document.querySelectorAll(
        'app-donation, app-sale, app-alert-display'
      );

      alertContainers.forEach((container) => {
        const htmlContainer = container as HTMLElement;
        
        // Resetar opacity e transforms
        htmlContainer.style.opacity = '0';
        htmlContainer.style.transform = 'scale(0)';
        
        // Remover classes de animação
        htmlContainer.classList.forEach((className) => {
          if (className.includes('animate') || className.includes('active')) {
            htmlContainer.classList.remove(className);
          }
        });
      });

      console.log('✅ Elementos de alerta limpos');
    } catch (error) {
      console.error('❌ Erro ao limpar elementos de alerta:', error);
    }
  }

  /**
   * Recovery completo - para tudo e reseta estado visual
   */
  performFullRecovery(): void {
    console.log('🔄 Iniciando recovery completo de animações...');
    
    this.stopAllHTMLAudio();
    this.stopSpeechSynthesis();
    this.stopAllVideos();
    this.resetFrozenAnimations();
    this.clearTemporaryAlertElements();
    
    console.log('✅ Recovery completo finalizado');
  }

  /**
   * Recovery parcial - apenas para mídia (sem alterar DOM)
   */
  stopAllMedia(): void {
    console.log('🔇 Parando todas as mídias...');
    
    this.stopAllHTMLAudio();
    this.stopSpeechSynthesis();
    this.stopAllVideos();
    
    console.log('✅ Todas as mídias paradas');
  }

  /**
   * Limpa um alerta específico por ID
   */
  clearAlertById(alertId: string): void {
    try {
      const alertElement = document.getElementById(alertId);
      
      if (alertElement) {
        console.log(`🗑️ Limpando alerta: ${alertId}`);
        
        // Fade out
        alertElement.style.opacity = '0';
        
        // Remover após animação
        setTimeout(() => {
          alertElement.remove();
        }, 300);
      }
    } catch (error) {
      console.error(`❌ Erro ao limpar alerta ${alertId}:`, error);
    }
  }
}
